import { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { User, Mail, Lock, Save, CheckCircle, XCircle, LogOut } from 'lucide-react';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { useNavigate } from 'react-router-dom';

interface UserProfile {
  _id: string;
  name: string;
  email: string;
  role: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

interface UpdateProfileData {
  name?: string;
  email?: string;
  currentPassword?: string;
  newPassword?: string;
}

interface ApiResponse {
  status: string;
  message: string;
  data: UserProfile;
}

const API_URL = import.meta.env.VITE_API_BASE_URL || '';

export default function Profile() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [formData, setFormData] = useState<UpdateProfileData>({
    name: '',
    email: '',
    currentPassword: '',
    newPassword: '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [statusMessage, setStatusMessage] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  // Fetch current user profile
  const { data: profile, isLoading } = useQuery<ApiResponse>({
    queryKey: ['profile'],
    queryFn: async () => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      const responseData = await response.json();

      if (!response.ok) {
        setStatusMessage({
          type: 'error',
          message: responseData.message || 'Error al cargar el perfil'
        });
        throw new Error(responseData.message || 'Error al cargar el perfil');
      }

      return responseData;
    },
  });

  // Update profile mutation
  const updateMutation = useMutation({
    mutationFn: async (data: UpdateProfileData): Promise<ApiResponse> => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      
      const responseData = await response.json();
      
      // Establecer el mensaje de estado basado en la respuesta
      setStatusMessage({
        type: responseData.status === 'success' ? 'success' : 'error',
        message: responseData.message
      });

      if (!response.ok) {
        throw new Error(responseData.message || 'Error al actualizar el perfil');
      }
      
      return responseData;
    },
    onSuccess: (response) => {
      // Actualizar el cache de React Query con los nuevos datos
      queryClient.setQueryData(['profile'], response);
      
      // Resetear el formulario de contraseña
      setIsChangingPassword(false);
      setFormData(prev => ({
        ...prev,
        name: response.data.name,
        email: response.data.email,
        currentPassword: '',
        newPassword: '',
      }));
    },
    onError: (error: Error) => {
      console.error('Error updating profile:', error);
    },
  });

  // Logout mutation
  const logoutMutation = useMutation({
    mutationFn: async () => {
      const token = sessionStorage.getItem('token');
      const response = await fetch(`${API_URL}/auth/logout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || 'Error al cerrar sesión');
      }
      
      return responseData;
    },
    onSuccess: (response) => {
      // Mostrar mensaje de éxito antes de limpiar la sesión
      setStatusMessage({
        type: 'success',
        message: response.message || 'Sesión cerrada exitosamente'
      });

      // Esperar un momento para que el usuario vea el mensaje
      setTimeout(() => {
        // Limpiar el token y los datos de la sesión
        sessionStorage.removeItem('token');
        queryClient.clear();
        navigate('/login');
      }, 1500);
    },
    onError: (error: Error) => {
      setStatusMessage({
        type: 'error',
        message: error.message
      });
      
      // Si el error es por sesión expirada, redirigir al login
      if (error.message.includes('sesión ha expirado')) {
        sessionStorage.removeItem('token');
        queryClient.clear();
        navigate('/login');
      }
    },
  });

  useEffect(() => {
    if (profile?.data) {
      setFormData(prev => ({
        ...prev,
        name: profile.data.name,
        email: profile.data.email,
      }));
    }
  }, [profile]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'El nombre es requerido';
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'El email es requerido';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Email inválido';
    }

    if (isChangingPassword) {
      if (!formData.currentPassword) {
        newErrors.currentPassword = 'La contraseña actual es requerida';
      }
      if (!formData.newPassword) {
        newErrors.newPassword = 'La nueva contraseña es requerida';
      } else if (formData.newPassword.length < 8) {
        newErrors.newPassword = 'La contraseña debe tener al menos 8 caracteres';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setStatusMessage(null); // Limpiar mensaje anterior
    
    if (validateForm()) {
      const updateData: UpdateProfileData = {
        name: formData.name,
        email: formData.email,
      };

      if (isChangingPassword) {
        updateData.currentPassword = formData.currentPassword;
        updateData.newPassword = formData.newPassword;
      }

      try {
        await updateMutation.mutateAsync(updateData);
      } catch (error) {
        console.error('Submit error:', error);
      }
    }
  };

  const handleLogout = async () => {
    try {
      await logoutMutation.mutateAsync();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="max-w-2xl mx-auto p-4">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-slate-200 rounded w-1/4"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
            <div className="h-32 bg-slate-200 rounded"></div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="border-b border-slate-200 p-6 flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Mi Perfil</h1>
              <p className="mt-1 text-sm text-slate-500">
                Gestiona tu información personal y preferencias
              </p>
            </div>
            <button
              onClick={handleLogout}
              disabled={logoutMutation.isPending}
              className={`
                inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
                ${logoutMutation.isPending
                  ? 'bg-red-400 cursor-not-allowed'
                  : 'bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500'
                }
              `}
            >
              <LogOut className="h-5 w-5 mr-2" />
              {logoutMutation.isPending ? 'Cerrando sesión...' : 'Cerrar Sesión'}
            </button>
          </div>

          {/* Status Message */}
          {statusMessage && (
            <div className={`p-4 ${
              statusMessage.type === 'success' 
                ? 'bg-green-50 border-green-200' 
                : 'bg-red-50 border-red-200'
              } border-l-4 flex items-center gap-2`}
            >
              {statusMessage.type === 'success' ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <span className={`text-sm ${
                statusMessage.type === 'success' 
                  ? 'text-green-700' 
                  : 'text-red-700'
                }`}
              >
                {statusMessage.message}
              </span>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-6 space-y-6">
            {/* Basic Info Section */}
            <div className="space-y-6">
              <h2 className="text-lg font-medium text-slate-900">
                Información Básica
              </h2>

              {/* Name */}
              <div>
                <label htmlFor="name" className="block text-sm font-medium text-slate-700">
                  Nombre
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="text"
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                    className={`
                      pl-10 block w-full rounded-md shadow-sm sm:text-sm
                      ${errors.name
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                      }
                    `}
                  />
                </div>
                {errors.name && (
                  <p className="mt-1 text-sm text-red-600">{errors.name}</p>
                )}
              </div>

              {/* Email */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-slate-700">
                  Email
                </label>
                <div className="mt-1 relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-5 w-5 text-slate-400" />
                  </div>
                  <input
                    type="email"
                    id="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    className={`
                      pl-10 block w-full rounded-md shadow-sm sm:text-sm
                      ${errors.email
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                        : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                      }
                    `}
                  />
                </div>
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600">{errors.email}</p>
                )}
              </div>
            </div>

            {/* Password Section */}
            <div className="pt-6 border-t border-slate-200">
              <h2 className="text-lg font-medium text-slate-900 mb-6">
                Cambiar Contraseña
              </h2>

              <button
                type="button"
                onClick={() => setIsChangingPassword(!isChangingPassword)}
                className="inline-flex items-center px-4 py-2 border border-slate-300 text-sm font-medium rounded-md text-slate-700 bg-white hover:bg-slate-50"
              >
                <Lock className="h-5 w-5 mr-2" />
                {isChangingPassword ? 'Cancelar cambio' : 'Cambiar contraseña'}
              </button>

              {isChangingPassword && (
                <div className="mt-4 space-y-4">
                  {/* Current Password */}
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-slate-700">
                      Contraseña Actual
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      value={formData.currentPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, currentPassword: e.target.value }))}
                      className={`
                        mt-1 block w-full rounded-md shadow-sm sm:text-sm
                        ${errors.currentPassword
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                        }
                      `}
                    />
                    {errors.currentPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.currentPassword}</p>
                    )}
                  </div>

                  {/* New Password */}
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-slate-700">
                      Nueva Contraseña
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      value={formData.newPassword}
                      onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                      className={`
                        mt-1 block w-full rounded-md shadow-sm sm:text-sm
                        ${errors.newPassword
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-500'
                          : 'border-slate-300 focus:border-primary-500 focus:ring-primary-500'
                        }
                      `}
                    />
                    {errors.newPassword && (
                      <p className="mt-1 text-sm text-red-600">{errors.newPassword}</p>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Submit Button */}
            <div className="pt-6 border-t border-slate-200">
              <button
                type="submit"
                disabled={updateMutation.isPending}
                className={`
                  inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white
                  ${updateMutation.isPending
                    ? 'bg-primary-400 cursor-not-allowed'
                    : 'bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500'
                  }
                `}
              >
                <Save className="h-5 w-5 mr-2" />
                {updateMutation.isPending ? 'Guardando...' : 'Guardar Cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </DashboardLayout>
  );
} 